import React, [useState, useEffect], from: 'react'
import [notes_path, note_path], from: 'config/routes.rb'
import JsonStreamProvider, [useJsonStream], from: 'juntos/components/JsonStreamProvider'

# SearchField component
def SearchField(value:, onChange:)
  handleChange = ->(e) { onChange(e.target.value) }

  %x{
    <input
      type="text"
      value={value}
      onChange={handleChange}
      placeholder="Search notes..."
      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  }
end

# NotePreview component for sidebar
def NotePreview(note:, selected:, onClick:)
  className = "p-3 border-b cursor-pointer hover:bg-gray-50"
  className += " bg-blue-50 border-l-4 border-l-blue-500" if selected

  %x{
    <div onClick={onClick} className={className}>
      <h3 className="font-medium truncate">{note.title}</h3>
      <p className="text-sm text-gray-500 truncate">{note.body}</p>
      <p className="text-xs text-gray-400 mt-1">
        {new Date(note.updated_at).toLocaleDateString()}
      </p>
    </div>
  }
end

# Editor component
def Editor(note:, onUpdate:, onDelete:)
  noteTitle, setNoteTitle = useState(note.title)
  noteBody, setNoteBody = useState(note.body)
  saving, setSaving = useState(false)

  # Reset when note changes
  useEffect(-> {
    setNoteTitle(note.title)
    setNoteBody(note.body)
    return  # useEffect must return undefined, not a value
  }, [note.id])

  handleTitleChange = ->(e) { setNoteTitle(e.target.value) }
  handleBodyChange = ->(e) { setNoteBody(e.target.value) }

  handleSave = -> {
    setSaving(true)
    onUpdate({ title: noteTitle, body: noteBody }).then(-> { setSaving(false) })
  }

  saveLabel = saving ? "Saving..." : "Save"

  %x{
    <div className="h-full flex flex-col">
      <input
        type="text"
        value={noteTitle}
        onChange={handleTitleChange}
        className="text-2xl font-bold border-0 border-b focus:outline-none focus:border-blue-500 pb-2 mb-4"
        placeholder="Note title"
      />
      <textarea
        value={noteBody}
        onChange={handleBodyChange}
        className="flex-1 resize-none border rounded p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Write your note..."
      />
      <div className="flex justify-between mt-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saveLabel}
        </button>
        <button
          onClick={onDelete}
          className="text-red-600 px-4 py-2 rounded hover:bg-red-50"
        >
          Delete
        </button>
      </div>
    </div>
  }
end

# NotesApp component - handles state and real-time updates
def NotesApp()
  notes, setNotes = useState([])
  selectedId, setSelectedId = useState(nil)
  searchQuery, setSearchQuery = useState("")
  loading, setLoading = useState(true)

  # Real-time updates from other windows/clients
  stream = useJsonStream()

  useEffect(-> {
    return unless stream.lastMessage
    case stream.lastMessage.type
    when "note_created"
      setNotes([stream.lastMessage.data, *notes])
    when "note_updated"
      setNotes(notes.map { |n| n.id == stream.lastMessage.data.id ? stream.lastMessage.data : n })
    when "note_destroyed"
      setNotes(notes.filter { |n| n.id != stream.lastMessage.data.id })
      setSelectedId(nil) if selectedId == stream.lastMessage.data.id
    end
    return  # useEffect must return undefined, not a value
  }, [stream.lastMessage])

  # Load notes on mount and when search changes
  useEffect(-> {
    setLoading(true)
    params = {}
    params[:q] = searchQuery if searchQuery.length > 0

    notes_path.get(params).json do |data|
      setNotes(data)
      setLoading(false)
    end
    return  # useEffect must return undefined, not a value (the .json call returns a Promise)
  }, [searchQuery])

  selectedNote = notes.find { |n| n.id == selectedId }

  handleCreate = -> {
    notes_path.post(note: { title: "Untitled", body: "..." }).json do |note|
      setNotes([note, *notes])
      setSelectedId(note.id)
    end
  }

  handleUpdate = ->(updates) {
    note_path(selectedId).patch(note: updates).json do |updated|
      setNotes(notes.map { |n| n.id == updated.id ? updated : n })
    end
  }

  handleDelete = -> {
    note_path(selectedId).delete.then do
      setNotes(notes.filter { |n| n.id != selectedId })
      setSelectedId(nil)
    end
  }

  handleSelectNote = ->(id) { setSelectedId(id) }

  renderNoteList = -> {
    if loading
      %x{<div className="p-4 text-gray-500 text-center">Loading...</div>}
    elsif notes.length == 0
      %x{<div className="p-4 text-gray-500 text-center">No notes found</div>}
    else
      notes.map do |note|
        onClick = -> { handleSelectNote(note.id) }
        %x{
          <NotePreview
            key={note.id}
            note={note}
            selected={note.id === selectedId}
            onClick={onClick}
          />
        }
      end
    end
  }

  renderEditor = -> {
    if selectedNote
      %x{
        <Editor
          note={selectedNote}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      }
    else
      %x{
        <div className="h-full flex items-center justify-center text-gray-500">
          Select a note or create a new one
        </div>
      }
    end
  }

  %x{
    <div className="flex h-screen bg-gray-100">
      <div className="w-80 bg-white border-r flex flex-col">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold mb-4">Notes</h1>
          <SearchField value={searchQuery} onChange={setSearchQuery} />
          <button
            onClick={handleCreate}
            className="mt-3 w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            New Note
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {renderNoteList()}
        </div>
      </div>
      <div className="flex-1 p-6">
        {renderEditor()}
      </div>
    </div>
  }
end

# Main Index component - wraps NotesApp in JsonStreamProvider for real-time updates
export default def Index()
  %x{
    <JsonStreamProvider stream="notes">
      <NotesApp />
    </JsonStreamProvider>
  }
end
